import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, clearToken } from "@/lib/api";
import { Database, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

// mode: "invite" -> /api/auth/set-password ; "reset" -> /api/auth/reset-password
export default function SetPassword({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const isInvite = mode === "invite";
  const endpoint = isInvite ? "/auth/set-password" : "/auth/reset-password";

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/validate-token?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.data.valid) { setState("valid"); setEmail(r.data.email); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(endpoint, { token, new_password: pwd });
      setState("done");
      toast.success("Password set — you can sign in now");
      // Hard redirect (and drop any existing session in this browser) so the
      // freshly-invited user reliably lands on the sign-in screen.
      localStorage.removeItem("cb_token");
      clearToken();
      setTimeout(() => { window.location.href = "/login"; }, 1400);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set password");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]">
      <div className="w-full max-w-sm" data-testid="set-password-page">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center rounded-sm">
            <Database className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight text-gray-900">ClientCases</span>
        </div>

        {state === "checking" && <div className="text-center text-gray-400 text-sm"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Validating link…</div>}

        {state === "invalid" && (
          <div className="text-center bg-white border border-gray-200 rounded-sm p-8" data-testid="token-invalid">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold text-gray-900">Link invalid or expired</h2>
            <p className="text-sm text-gray-500 mt-2">Ask an administrator for a new invite, or use “Forgot password”.</p>
            <button onClick={() => navigate("/login")} className="mt-5 text-[#002FA7] font-semibold text-sm hover:underline">Back to sign in</button>
          </div>
        )}

        {state === "done" && (
          <div className="text-center bg-white border border-gray-200 rounded-sm p-8">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold text-gray-900">All set!</h2>
            <p className="text-sm text-gray-500 mt-2">Redirecting you to sign in…</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-7" data-testid="set-password-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-gray-900">
              {isInvite ? "Welcome — set your password" : "Choose a new password"}
            </h2>
            <p className="text-gray-500 text-sm mt-1.5 mb-6">{email}</p>
            <label className="block text-sm font-semibold text-gray-700 mb-1">New password</label>
            <input data-testid="set-new-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} className={`${inputCls} mb-4`} />
            <label className="block text-sm font-semibold text-gray-700 mb-1">Confirm password</label>
            <input data-testid="set-confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={`${inputCls} mb-6`} />
            <button type="submit" disabled={saving} data-testid="set-password-submit"
              className="w-full bg-[#002FA7] hover:bg-[#002480] text-white py-2.5 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isInvite ? "Set password & continue" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
